import React, { useState, useEffect, useRef, useCallback } from "react";
import Steps from "../../Steps";
import Button from "../../../../common/Button";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { classificationResultCalculation, preventionData } from "./data";
import { updateClinicalService } from "../../../../../redux/modules/clinical-services/actions";
import { ReactSVG } from "react-svg";
import clsx from "clsx";
import SubNavigation from "../../common/sub-navigation";
import MyResults from "../other/my-results";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import { getValidStep } from "../../../text/data";

const Prevention = () => {
  const { clinicalServiceObj, clinicalServiceSteps } = useSelector(
    (state) => state.clinicalService
  );
  const dispatch = useDispatch();
  const { serviceId } = useParams();
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;

  const [flippedCards, setFlipCards] = useState([]);
  const [completedGoalsOfTherapy, setCompletedGoalsOfTherapy] = useState(false);
  const [completedFlippedCards, setCompletedFlippedCards] = useState([]);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const preventionContainerRef = useRef(null);
  const preventionCardRefs = useRef([]);
  const autoScrollInProgressRef = useRef(false);
  const autoScrollTimeoutRef = useRef(null);
  const scrollTickingRef = useRef(false);
  const { frequencyOfSymptoms = null, levelOfSeverity = null } =
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.treatment ||
    {};
  const { showReferral } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const [showRef, setShowRef] = useState(showReferral ?? false);

  const ensureCardVisibility = useCallback((index, behavior = "smooth") => {
    const container = preventionContainerRef.current;
    const card = preventionCardRefs.current[index];

    if (!container || !card) {
      return;
    }

    const tolerancePx = 4;
    const boundaryTolerancePx = 8;
    const cardGapPx = 24; // Keeps current card gap in sync with `gap-6`.
    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const containerRect = container.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const isHiddenOnLeft = cardRect.left < containerRect.left - tolerancePx;
    const isHiddenOnRight = cardRect.right > containerRect.right + tolerancePx;
    const isAtRightBoundary = cardRect.right >= containerRect.right - boundaryTolerancePx;
    const nextCard = preventionCardRefs.current[index + 1];
    const nextCardRect = nextCard?.getBoundingClientRect();
    const isNextCardPartiallyVisibleOnRight =
      !!nextCardRect &&
      nextCardRect.left < containerRect.right - tolerancePx &&
      nextCardRect.right > containerRect.right + tolerancePx;
    let targetScrollLeft = null;

    if (isHiddenOnLeft) {
      targetScrollLeft = card.offsetLeft;
    } else if (isHiddenOnRight) {
      // If the clicked/focused card is partially visible on the right, bring that card fully into view.
      targetScrollLeft = card.offsetLeft;
    } else if (isNextCardPartiallyVisibleOnRight) {
      // Sequential navigation case: current card is the last fully visible one, next card is peeking in.
      targetScrollLeft = nextCard.offsetLeft;
    } else if (isAtRightBoundary) {
      // When card reaches right boundary, push viewport to reveal upcoming card.
      targetScrollLeft = card.offsetLeft + card.offsetWidth + cardGapPx - container.clientWidth;
    }

    if (targetScrollLeft === null) {
      return;
    }

    const boundedScrollLeft = Math.min(Math.max(targetScrollLeft, 0), maxScrollLeft);

    if (Math.abs(container.scrollLeft - boundedScrollLeft) < 2) {
      return;
    }

    autoScrollInProgressRef.current = true;
    container.scrollTo({ left: boundedScrollLeft, behavior });
    window.clearTimeout(autoScrollTimeoutRef.current);
    autoScrollTimeoutRef.current = window.setTimeout(() => {
      autoScrollInProgressRef.current = false;
    }, 350);
  }, []);

  useEffect(() => {
    let obj =
      clinicalServiceObj.initialAssessment.initialAssessmentForm.education;
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm !==
        undefined &&
      obj !== null &&
      obj !== undefined
    ) {
      setCompletedGoalsOfTherapy(obj?.preventionTipsComplete);
      setCompletedFlippedCards(preventionData.map((_, index) => index));
    }
  }, [clinicalServiceObj.initialAssessment.initialAssessmentForm]);

  const onNext = () => {
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      1
    );
    const updatedSteps = {
      education: {
        preventionTipsComplete: completedGoalsOfTherapy,
      },
      currentStep: nextStep,
      currentSubStep: nextSubStep,
      serviceSteps: clinicalServiceSteps,
    };
    dispatch(updateClinicalService(serviceId, updatedSteps));
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };
    dispatch(updateClinicalService(serviceId, updatedSteps));
  };

  const handleFlipCard = (index, isIconClick = false) => {
    let temp = [...flippedCards];

    if (isIconClick) {
      // If flip.svg icon is clicked, toggle only the clicked card
      if (temp.includes(index)) {
        temp = []; // Close the currently opened card
      } else {
        temp = [index]; // Open only the clicked card
      }
    } else {
      // Handle card flip behavior for the card itself
      if (temp.includes(index)) {
        temp = temp.filter((item) => item !== index);
      } else {
        temp.push(index);
      }
    }

    // Update flipped cards and ensure no duplicate in completed flipped cards
    setFlipCards(temp);
    setCompletedFlippedCards([...new Set([...completedFlippedCards, index])]);
    setActiveCardIndex(index);
  };

  useEffect(() => {
    const totalCards = preventionData.length; // Get total cards from data
    const allCardsFlipped = Array.from({ length: totalCards }, (_, i) =>
      completedFlippedCards.includes(i)
    ).every(Boolean);

    setCompletedGoalsOfTherapy(allCardsFlipped);
  }, [completedFlippedCards]);

  useEffect(() => {
    ensureCardVisibility(activeCardIndex);
  }, [activeCardIndex, ensureCardVisibility]);

  useEffect(() => {
    const container = preventionContainerRef.current;

    if (!container) {
      return undefined;
    }

    const onScroll = () => {
      if (autoScrollInProgressRef.current || scrollTickingRef.current) {
        return;
      }

      scrollTickingRef.current = true;
      window.requestAnimationFrame(() => {
        ensureCardVisibility(activeCardIndex);
        scrollTickingRef.current = false;
      });
    };

    const onResize = () => {
      ensureCardVisibility(activeCardIndex, "auto");
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      container.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.clearTimeout(autoScrollTimeoutRef.current);
    };
  }, [activeCardIndex, ensureCardVisibility]);

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 overflow-auto">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  Prevention Tips
                  <span className="text-xl-body font-normal ml-3">
                    Avoid Allergens and Irritants - First Line Therapy
                  </span>
                </h4>
                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div className="flex flex-col">
                <div className="flex justify-between">
                  {true && (
                    <MyResults
                      frequencyOfSymptoms={frequencyOfSymptoms}
                      levelOfSeverity={levelOfSeverity}
                    />
                  )}
                </div>
                <div
                  className="flex flex-row gap-6 mt-6 overflow-auto scroll-smooth"
                  id="prevention"
                  ref={preventionContainerRef}
                >
                  {preventionData.map((prevention, index) => (
                    <div
                      key={index}
                      ref={(element) => {
                        preventionCardRefs.current[index] = element;
                      }}
                      tabIndex={0}
                      className={clsx(
                        "flex flex-col relative max-h-[415px] min-h-[415px] min-w-[370px] max-w-[370px] rounded-[12px] px-[20px] bg-gray-20 cursor-pointer"
                      )}
                      onClick={() => handleFlipCard(index)}
                      onFocus={() => setActiveCardIndex(index)}
                      onKeyDown={(event) => {
                        if (event.key === "ArrowRight" && index < preventionData.length - 1) {
                          event.preventDefault();
                          const nextIndex = index + 1;
                          preventionCardRefs.current[nextIndex]?.focus();
                          setActiveCardIndex(nextIndex);
                        }

                        if (event.key === "ArrowLeft" && index > 0) {
                          event.preventDefault();
                          const previousIndex = index - 1;
                          preventionCardRefs.current[previousIndex]?.focus();
                          setActiveCardIndex(previousIndex);
                        }
                      }}
                    >
                      {flippedCards.includes(index) ? (
                        <>
                            <ReactSVG
                              src={prevention.alterImage}
                              className="mt-[62px] mx-auto h-[80px]"
                            />
                          {prevention.render()}
                        </>
                      ) : (
                        <ReactSVG
                          src={prevention.mainImage}
                          className="w-full max-w-[196px] m-auto"
                        />
                      )}
                      <ReactSVG
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click event
                          handleFlipCard(index, true);
                        }}
                        src={"/icons/flip.svg"}
                        className="w-[20px] h-[20px] absolute cursor-pointer bottom-[10px] right-[10px]"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between space-x-4 rounded">
        <div className="w-full">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={onPrevious}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px] ${
              !completedGoalsOfTherapy && "opacity-40 pointer-events-none"
            }`}
            onClick={onNext}
          />
        </div>
      </div>
    </div>
  );
};

export default Prevention;
